import React, { useEffect, useMemo, useState } from "react";
import { Text, Flex, Tabs, Tab, SlideoutDrawerV2 } from "@jsq/ds/core";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { PaymentStatus } from "#graphql/supergraphEnums";
import { useGetPaymentBatchLogsLazyQuery } from "#graphql/supergraphHooks";
import { clientContext, toDbId } from "#utils/graphql_utils";
import { PaymentDetailsCard } from "../../../PayableWorkflow/components/PaymentDetailsCard";
import { DocumentTracker } from "../../../PayeeWorkflows/components/DocumentTracker";
import MarkAsFailedModal from "../../../PositionsTable/MarkAsFailedModal";
import { CombinedStatusRenderer } from "../../../shared/CombinedStatusRenderer";
import { LogsList } from "../../../shared/LogsList";
import { usePaymentsContext } from "../../Context/PaymentsContext";
import { InvoiceDetailsCard } from "../InvoiceDetailsCard";
import { payableLogsConfig } from "../paymentBatchLogsConfig";
import { PaymentBatchReviewInfoBanner, usePaymentBatchReview } from "../PaymentBatchReview";
import { PayablePaymentBatchDetails } from "../types";
import { PayeePendingBanner } from "./PayeePendingBanner";
import {
  slideoutContainer,
  leftColumn,
  rightColumn,
  rightColumnTabsContainer,
  rightColumnContent,
  rightColumnHeader,
  rightColumnTabs,
  slideoutHeader,
} from "./styles_vanilla.css";

enum PayablePaymentDetailsTabs {
  Overview = "Overview",
  Log = "Log",
}

export type PayablePaymentDetailsDisplayProps = {
  payablePaymentBatchDetails: PayablePaymentBatchDetails;
};

export const PayablePaymentDetailsDisplay: React.FC<PayablePaymentDetailsDisplayProps> = ({
  payablePaymentBatchDetails,
}: PayablePaymentDetailsDisplayProps) => {
  const [currentTab, setCurrentTab] = useState<PayablePaymentDetailsTabs>(
    PayablePaymentDetailsTabs.Overview,
  );
  const [showMarkAsFailedModal, setShowMarkAsFailedModal] = useState(false);

  const { approveButton, denyButton, denyModal } = usePaymentBatchReview({
    batchId: payablePaymentBatchDetails.id,
    reviewState: payablePaymentBatchDetails.reviewState,
  });

  const [fetchLogs, { data: logsData, loading: logsLoading, error: logsError }] =
    useGetPaymentBatchLogsLazyQuery({
      fetchPolicy: "cache-and-network",
      context: clientContext(),
    });

  useReportError(logsError ? formatError(logsError) : null);

  const logs = logsData?.paymentBatch?.logs || [];

  // Fetch logs when Log tab is selected
  useEffect(() => {
    if (currentTab === PayablePaymentDetailsTabs.Log && !logsData) {
      void fetchLogs({
        variables: {
          paymentBatchId: payablePaymentBatchDetails.id,
        },
      });
    }
  }, [currentTab, logsData, fetchLogs, payablePaymentBatchDetails.id]);

  const { isGPX } = usePaymentsContext();

  // Expense payments will only ever have one payment
  const payment = payablePaymentBatchDetails.payments[0];
  const paymentStatus = payment?.status as PaymentStatus | undefined;
  const paymentId = payment?.id;

  // Mark as failed is only enabled for PROCESSING or SETTLED payments
  const isMarkAsFailedEnabled =
    paymentStatus === PaymentStatus.Processing || paymentStatus === PaymentStatus.Settled;

  const bankAccountUrl = useMemo<string | null>(() => {
    const bankAccountId = payablePaymentBatchDetails.bankAccount?.id;
    if (!bankAccountId) return null;
    return `https://${payablePaymentBatchDetails.hostName}/admin_settings/bank_account/${toDbId(
      bankAccountId,
    )}`;
  }, [payablePaymentBatchDetails.hostName, payablePaymentBatchDetails.bankAccount?.id]);

  return (
    <Flex className={slideoutContainer}>
      <Flex className={leftColumn}>
        {payablePaymentBatchDetails.supportingDocuments.length > 0 ? (
          <DocumentTracker
            documentName={payablePaymentBatchDetails.supportingDocuments[0].name}
            documentUrl={payablePaymentBatchDetails.supportingDocuments[0].viewUrl}
            slideoutMode={true}
          />
        ) : (
          <Flex flexDirection="column" alignItems="center" justifyContent="center">
            <Text variant="regular-md" color="muted">
              No supporting documents available for this invoice.
            </Text>
          </Flex>
        )}
      </Flex>

      <Flex className={rightColumn}>
        <Flex className={rightColumnHeader}>
          <SlideoutDrawerV2.ComplexHeader
            title={payablePaymentBatchDetails.payeeName}
            subtitles={[
              <Flex key="status" flexDirection="row" gap={32}>
                <Flex flexDirection="column" gap={4}>
                  <Text key="subtitle" variant="secondary-md" marginBottom="md">
                    {payablePaymentBatchDetails.payeeType[0].toUpperCase() +
                      payablePaymentBatchDetails.payeeType.slice(1) +
                      " payment"}
                  </Text>
                  <CombinedStatusRenderer
                    config={payablePaymentBatchDetails.combinedStatusConfig}
                  />
                  <Text variant="secondary-md">Status</Text>
                </Flex>
              </Flex>,
            ]}
            renderMenu={!isGPX && paymentId !== undefined}
            menuItems={[
              {
                label: "Mark payment as failed",
                onClick: () => setShowMarkAsFailedModal(true),
                isDisabled: !isMarkAsFailedEnabled,
              },
            ]}
            className={slideoutHeader}
          />
        </Flex>

        <Flex className={rightColumnTabsContainer}>
          <Tabs className={rightColumnTabs}>
            <Tab
              role="tab"
              text={PayablePaymentDetailsTabs.Overview}
              onClick={() => setCurrentTab(PayablePaymentDetailsTabs.Overview)}
              active={currentTab === PayablePaymentDetailsTabs.Overview}
            />
            <Tab
              role="tab"
              text={PayablePaymentDetailsTabs.Log}
              onClick={() => setCurrentTab(PayablePaymentDetailsTabs.Log)}
              active={currentTab === PayablePaymentDetailsTabs.Log}
            />
          </Tabs>
        </Flex>

        <Flex flexDirection="column" className={rightColumnContent} gap={16}>
          {currentTab === PayablePaymentDetailsTabs.Overview && (
            <>
              <PaymentBatchReviewInfoBanner
                isGPX={isGPX}
                reviewState={payablePaymentBatchDetails.reviewState}
              />
              <PayeePendingBanner
                payeeStatus={payablePaymentBatchDetails.payeeStatus}
                payeeId={payablePaymentBatchDetails.payeeId}
                watchlistStatus={payablePaymentBatchDetails.watchlistStatus}
              />
              <PaymentDetailsCard
                bankAccount={payablePaymentBatchDetails.bankAccount}
                entity={payablePaymentBatchDetails.entity}
                createdDate={payablePaymentBatchDetails.createdDate}
                paymentDate={payablePaymentBatchDetails.paymentDate}
                paymentAmount={payablePaymentBatchDetails.paymentAmount}
                paymentDescription={payablePaymentBatchDetails.paymentDescription}
                payeeId={payablePaymentBatchDetails.payeeId}
                referenceNumber={payablePaymentBatchDetails.referenceNumber}
                bankAccountUrl={bankAccountUrl}
              />

              {payablePaymentBatchDetails.payeeType === "vendor" && (
                <InvoiceDetailsCard
                  vendorName={payablePaymentBatchDetails.payeeName}
                  entityName={payablePaymentBatchDetails.entity.name}
                  invoiceNumber={payablePaymentBatchDetails.invoiceNumber}
                  invoiceAmount={payablePaymentBatchDetails.invoiceAmount}
                  invoiceDate={payablePaymentBatchDetails.invoiceDate}
                  dueDate={payablePaymentBatchDetails.dueDate}
                  invoiceDescription={payablePaymentBatchDetails.paymentDescription}
                />
              )}
            </>
          )}
          {currentTab === PayablePaymentDetailsTabs.Log && (
            <LogsList
              logs={logs}
              loading={logsLoading}
              error={logsError}
              config={payableLogsConfig}
            />
          )}
        </Flex>
        <SlideoutDrawerV2.ComplexFooter
          secondaryButton={denyButton}
          primaryButton={approveButton}
        />
      </Flex>
      {denyModal}
      <MarkAsFailedModal
        paymentId={paymentId || ""}
        payeeName={payablePaymentBatchDetails.payeeName}
        show={showMarkAsFailedModal}
        onHide={() => setShowMarkAsFailedModal(false)}
      />
    </Flex>
  );
};
